import React from "react";
import { PlayIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "./ui/badge";
import SeriesThumbnail from "./SeriesThumbnail";
import SeriesLink from "./SeriesLink";
import type { DetailsPageMode } from "./SeriesLink";
import FadeInImage from "@/components/FadeInImage";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import useFormat from "@/hooks/useFormat";

const counterSizeToIcon = {
  xs: "size-3",
  sm: "size-4",
  lg: "size-5",
} as const;

const counterSizeToText = {
  xs: "text-[10px]",
  sm: "text-sm",
  lg: "text-base",
} as const;

const SeriesCover: React.FC<
  React.ComponentProps<"div"> & {
    badge?: string;
    thumbnail: string;
    views?: number;
    seriesId: string;
    resumeEpisodeId?: string;
    // Source `show.title` — identity only: the Amplitude "series name" and the
    // URL slug (see SeriesLink). Never render it: it is not localized.
    title: string | undefined;
    // Localized `show.displayTitle` — names the cover link (SHO-663).
    displayTitle: string;
    episodeCount?: number;
    origin?: string;
    /** See SeriesLink: a catalog entry point under the SHO-758 A/B test. */
    detailsPage?: DetailsPageMode;
    size?: "xs" | "sm" | "md" | "lg" | "default";
    counterSize?: "xs" | "sm" | "lg";
    titleImage?: string;
    watchNow?: boolean;
    thumbnailHeight?: { mobile: string; desktop: string };
    priority?: boolean;
  }
> = ({
  className,
  badge,
  thumbnail,
  views,
  seriesId,
  resumeEpisodeId,
  title,
  displayTitle,
  episodeCount,
  titleImage,
  origin,
  detailsPage,
  size = "default",
  counterSize = "sm",
  watchNow = false,
  thumbnailHeight = { mobile: "320", desktop: "540" },
  priority = false,
  ...props
}) => {
  const { t } = useTranslation();
  const { formatViewsNumber, getResizedThumbnailUrl } = useFormat();

  return (
    <div className={cn("group/cover", className)} {...props}>
      <SeriesLink
        seriesId={seriesId}
        resumeEpisodeId={resumeEpisodeId}
        title={title}
        episodeCount={episodeCount}
        origin={origin}
        detailsPage={detailsPage}
      >
        <SeriesThumbnail
          thumbnailHeights={thumbnailHeight}
          size={size}
          thumbnail={thumbnail}
          alt={displayTitle}
          priority={priority}
          className="h-full w-full"
        />
      </SeriesLink>
      {badge && (
        <Badge
          size={size === "sm" ? "sm" : "default"}
          className={cn(
            "absolute top-3 right-3",
            size === "sm" && "top-1 right-1",
          )}
        >
          {badge}
        </Badge>
      )}

      {titleImage && (
        <FadeInImage
          // Decorative: the title it renders is already carried by the cover
          // link's `alt` above, and by the heading FeaturedCover shows next to it.
          alt=""
          src={getResizedThumbnailUrl(titleImage, "320")}
          loading="lazy"
          className="bottom-0 left-0 w-full -translate-y-full object-cover"
        />
      )}

      {views !== undefined && views > 0 && (
        <div className="absolute right-0 bottom-0 flex w-full items-center justify-end gap-1 rounded-sm bg-linear-to-b from-transparent to-black/60 p-2 text-white">
          <PlayIcon
            className={cn("fill-current", counterSizeToIcon[counterSize])}
          />
          <p className={cn("font-medium", counterSizeToText[counterSize])}>
            {formatViewsNumber(views)}
          </p>
        </div>
      )}
      {watchNow && (
        <div
          className={cn(
            "from-primary to-brand-30 absolute right-0 bottom-0 left-0 flex w-full items-center justify-center gap-1 bg-linear-to-b p-2 text-white",
            size === "xs" && "rounded-b-xs",
            size === "sm" && "rounded-b-sm",
            size === "md" && "rounded-b-md",
            size === "lg" && "rounded-b-lg",
            size === "default" && "rounded-b",
          )}
        >
          <PlayIcon className="size-4 fill-current" />
          <p className="text-body font-semibold uppercase">
            {t("series_cover_watch_button")}
          </p>
        </div>
      )}
    </div>
  );
};

const sizeToRounded = {
  xs: "rounded-sm",
  sm: "rounded-sm",
  default: "rounded",
  md: "rounded-md",
  lg: "rounded-lg",
} as const;

const SeriesCoverSkeleton: React.FC<
  React.ComponentProps<"div"> & {
    size?: "xs" | "sm" | "md" | "lg" | "default";
  }
> = ({ className, size = "default", ...props }) => (
  <Skeleton
    className={cn(
      "aspect-3/4 inset-shadow-[0_0_0_1px] inset-shadow-white/20",
      sizeToRounded[size],
      className,
    )}
    {...props}
  />
);

export default Object.assign(SeriesCover, { Skeleton: SeriesCoverSkeleton });
